import * as uuid from "uuid";
import { AsyncStorageType } from "./types";

declare global {
  interface Window {
    webkit: any;
    onReceiveMessage: any;
  }
}

type PromiseArguments = {
  resolve: (data?: any) => void;
  reject: (error?: any) => void;
};

export type MessageData = {
  key: string;
  value?: string;
};

type AsyncStorageOptions = {
  sendMessage: (type: string, data: MessageData) => Promise<any>;
};

// a factory to keep the implementation testable
export const createAsyncStorage = ({ sendMessage }: AsyncStorageOptions): AsyncStorageType => {
  const getItem = async (key: string) => {
    const json = await sendMessage("get_item", { key });
    return json ? JSON.parse(json) : undefined;
  };

  const setItem = async (key: string, value: any) => {
    const json = JSON.stringify(value);
    return sendMessage("set_item", { key, value: json });
  };

  const removeItem = async (key: string) => {
    return sendMessage("remove_item", { key });
  };

  return {
    getItem,
    setItem,
    removeItem,
  };
};

// Store promise functions by handle id.
// Any messages we send get a handle id so we can find their response in onReceiveMessage.
const handlers: Record<string, PromiseArguments> = {};

// We receive messages from iOS with the handle id we sent the message with.
export const onReceiveMessage = (handleId: string, data: unknown, error: unknown): void => {
  const handler = handlers[handleId];
  if (error) {
    handler.reject(error);
  } else {
    handler.resolve(data);
  }
  delete handlers[handleId];
};

type SendMessageSignature = (type: string, data: MessageData) => Promise<string | null>;

// this is how we communicate with iOS
export const createSendMessage = (): SendMessageSignature => {
  window.onReceiveMessage = onReceiveMessage;

  // Wrap the message handler in a Promise so we can asynchronosuly handle messages by handle id.
  return (type: string, data: MessageData): Promise<string | null> => {
    return new Promise((resolve, reject) => {
      const handleId = uuid.v4();
      handlers[handleId] = { resolve, reject };

      // This is the message handler defined from the iOS code.
      // It lets us send messages to iOS code from JS.
      const messageHandler = window.webkit?.messageHandlers?.SenseIOSCommunicationInterface;

      if (messageHandler) {
        messageHandler.postMessage({
          handleId,
          type,
          data,
        });
      } else {
        // this is so that unit tests work
        window.onReceiveMessage(handleId, true, false);
      }
    });
  };
};

export const sendMessage = createSendMessage();

export default createAsyncStorage({ sendMessage });
